import { makeEnum, makeOptions,
  setupClass, lookupData, 
} from '../Base.js'
import { generateItemName, formatNum, resOr, sortItemsByName, } from '../SharedUtils.js'
import { InputComponent } from '../Common/InputComponent.js'

import { DoorsData } from '../MaterialData/DoorsData.js'
import { Units } from '../Common/Units.js'

import { prettyJson, valOr,
} from '../SharedUtils.js'
export { Units } from '../Common/Units.js'

import {
  FieldType,
  Field,
} from '../Common/Field.js'

import {
  kDirectionChoices, YesNo,
  makeYesNoField, makeNoYesField,
 } from './Common.js'

 import {
  WindowType, WindowStyle, WindowBuilder
 } from './WindowType.js'

import { gApp, DebugOn } from '../Globals.js'


export let DoorInputType = makeEnum({
  Manual: 'Manual',
  BuildDoor: 'Build door',
})

let ScreenDoorTypes = makeEnum({
  None: 'None',
  Inside: 'Inside',
  Outside: 'Outside',
})

export let DoorColor = makeEnum({
  Light: 'Light',
  Medium: 'Medium',
  Dark: 'Dark',
})

export let DoorBuildType = makeEnum({
  SwingingDoor: 'Swinging door',
  SlidingGlassDoor: 'Sliding glass door',
  StileAndRailDoor: 'Swinging stile and rail glass door',
  RevolvingDoor: 'Revolving door',
  SteelEmergencyExitDoor: 'Steel emergency exit door',
  SteelSectional: 'Steel section / Tilt-up',
  AircraftHangarDoor: 'Aircraft hangar door',
});

let SwingingDoorType = makeEnum({
  WoodSlabInWoodFrame: 'Wood slab in wood frame',
  InsulatedSteelSlabWithWoodEdgeInWoodFrame: 'Insulated steel slab with wood edge in wood frame',
  InsulatedSteelSlabWithMetalEdgeInMetalFrame: 'Insulated steel slab with metal edge in metal frame',
  CardboardHoneycombSlab: 'Cardboard honeycomb slab with metal edge in metal frame',
})

let DoorGlassType = makeEnum({
  SinglePane: 'Single-pane',
  DoublePane: 'Double-pane',
  DoublePaneLowE: 'Double-pane, low-e',
})

class SwingingDoor {
  init() {
    let door = this;
    this.type = Field.makeSelect('Type', SwingingDoorType, {
      note: 'The construction type of the swinging door slab and frame.',
    })
    this.percentGlazing = new Field({
      name: 'Percent Glass',
      type: FieldType.Percent,
      note: 'The percentage of the door area that is glass. Set to 0 if the door is fully opaque.',
    })
    this.glassType = Field.makeSelect('Glass type', DoorGlassType, {
      note: 'The type of glass used in the door. Only relevant when Percent Glass is greater than 0.',
    })
    this.glassType.makeUpdater((field) => {
      field.visible = door.percentGlazing.value > 0;
    })
    this.thermalBreak = Field.makeSelect('Thermal break', YesNo, {
      note: 'Whether the door has a thermal break (a non-conductive material separating the inner and outer parts of the frame). Applies to metal-framed doors.',
    })

    this.fields = [
      'type',
      'percentGlazing',
      'glassType',
      'thermalBreak',
    ]
    this.serFields = this.fields;
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Swinging Door',
    }
  }
}
setupClass(SwingingDoor)

let AluminumDoorFrameType = makeEnum({
  AluminumWithoutThermalBreak: 'Aluminum without thermal break',
  AluminumWithThermalBreak: 'Aluminum with thermal break',
})

class SlidingGlassDoor {
  init() {
    this.windowBuilder = WindowBuilder.create(this, {isSlidingGlassDoor: true});

    this.serFields = [
      'windowBuilder',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Sliding Glass Door',
    }
  }
}
setupClass(SlidingGlassDoor)

class StileAndRailDoor {
  init() {
    let door = this;
    this.frameType = Field.makeSelect('Door frame type', AluminumDoorFrameType, {
      note: 'The frame type of the stile and rail door. Choose whether the aluminum frame has a thermal break.',
    })
    // Note: we assume that this door type is 100% glass
    this.glassType = Field.makeSelect('Glass type', DoorGlassType, {
      note: 'The type of glass used in the door. Stile and rail doors are assumed to be 100% glass.',
    })
    // this.thermalBreak = Field.makeSelect('Thermal break', YesNo)

    this.fields = [
      'frameType',
      'glassType',
      // 'thermalBreak',
    ]
    this.serFields = this.fields;
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Stile and Rail Door',
    }
  }

  getPercentGlazing() {
    // Note: we originally had this door allow some user-given % glass, but
    // now it is always 100%.
    return 100.0;
  }
}
setupClass(StileAndRailDoor)

let RevolvingDoorType = makeEnum({
  ThreeWing: '3-wing',
  FourWing: '4-wing',
})

let RevolvingDoorSize = makeEnum({
  n8by7: '8ft x 7ft (2.4m x 2.1m)',
  n10by8: '10ft x 8ft (3m x 2.4m)',
  n7by6p5: '7ft x 6.5ft (2.1m x 2m)',
  n7by7p5: '7ft by 7.5ft (2.1m x 2.3m)',
})

class RevolvingDoor {
  init() {
    let door = this;
    this.type = Field.makeSelect('Type', RevolvingDoorType, {
      note: 'The number of wings on the revolving door.',
    })
    this.size = Field.makeSelect('Size', RevolvingDoorSize, {
      note: 'The size of the revolving door. The available options depend on the number of wings.',
      notesData: {
        'AllOptions': makeOptions(RevolvingDoorSize),
      },
    });
    this.size.makeChoicesUpdater(() => {
      if (door.type.value == RevolvingDoorType.ThreeWing) {
        return makeOptions(RevolvingDoorSize, [
          RevolvingDoorSize.n8by7,
          RevolvingDoorSize.n10by8,
        ])
      } else if (door.type.value == RevolvingDoorType.FourWing) {
        return makeOptions(RevolvingDoorSize, [
          RevolvingDoorSize.n7by6p5,
          RevolvingDoorSize.n7by7p5,
        ])
      }
      throw new Error("Uknown revolving door type");
    })

    this.fields = [
      'type',
      'size',
    ]
    this.serFields = this.fields;
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'RevolvingDoor',
    }
  }
}
setupClass(RevolvingDoor)

let SteelEmergExitInsulation = makeEnum({
  HoneycombKraftPaper: 'Honeycomb kraft paper',
  MineralWoolWithSteelRibs: 'Mineral wool with steel ribs',
  PolyurethaneFoam: 'Polyurethane foam',
})

let SteelEmergExitInsulationThickness = makeEnum({
  One3f8: '1 3/8" (35mm)',
  One3f4: '1 3/4" (44mm)',
})

let SteelEmergExitNumDoors = makeEnum({
  Single: 'Single',
  Double: 'Double',
})

class SteelEmergencyExitDoor {
  init() {
    this.insulation = Field.makeSelect('Insulation', SteelEmergExitInsulation, {
      note: 'The insulation material used inside the steel emergency exit door.',
    })
    this.insulationThickness = Field.makeSelect('Insulation Thickness',
      SteelEmergExitInsulationThickness, {
      note: 'The thickness of the insulation inside the door.',
    })
    this.thermalBreak = Field.makeSelect('Thermal break', YesNo, {
      note: 'Whether the steel door frame has a thermal break (a non-conductive material separating the inner and outer parts of the frame).',
    })
    this.numDoors = Field.makeSelect('Number of doors', SteelEmergExitNumDoors, {
      note: 'Whether the exit is a single door or a pair of double doors.',
    })

    this.fields = [
      'insulation',
      'insulationThickness',
      'thermalBreak',
      'numDoors',
    ]

    this.serFields = this.fields;
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Steel Emergency Exit Door',
    }
  }
}
setupClass(SteelEmergencyExitDoor)

let SteelSectionalInsulationType = makeEnum({
  PolyurethaneThermallyBroken: 'Polyurethane, thermally broken',
  ExtrudedPolystreneWithSteelRibs: 'Extruded polystrene with steel ribs',
  ExpandedPolystreneWithSteelRibs: 'Expanded polystrene with steel ribs',
  Uninsulated: 'Uninsulated',
})

let SteelSectionalInsulationThickness = makeEnum({
  One_3f4: '1 3/4" (44mm)',
  One_3f8: '1 3/8" (35mm)',
  Two: '2" (51mm)',
  Three: '3" (76mm)',
  Four: '4" (102mm)',
  Six: '6" (153mm)',
})

class SteelSectionalDoor {
  init() {
    let door = this;
    this.insulationType = Field.makeSelect('Insulation type', SteelSectionalInsulationType, {
      note: 'The insulation material used in the steel sectional door.',
    })
    this.insulationThickness = Field.makeSelect('Insulation thickness', SteelSectionalInsulationThickness, {
      note: 'The thickness of the insulation. The available options depend on the insulation type. Hidden when the door is uninsulated.',
      notesData: {
        'AllOptions': makeOptions(SteelSectionalInsulationThickness),
      },
    })
    this.insulationThickness.makeChoicesUpdater(() => {
      if (door.insulationType.value == SteelSectionalInsulationType.PolyurethaneThermallyBroken) {
        return makeOptions(SteelSectionalInsulationThickness, [
          SteelSectionalInsulationThickness.One_3f4,
        ]);
      } else {
        return makeOptions(SteelSectionalInsulationThickness, [
          SteelSectionalInsulationThickness.One_3f8,
          SteelSectionalInsulationThickness.Two,
          SteelSectionalInsulationThickness.Three,
          SteelSectionalInsulationThickness.Four,
          SteelSectionalInsulationThickness.Six,
        ])
      }
    })
    this.insulationThickness.setVisibility(() => {
      return door.insulationType.value != SteelSectionalInsulationType.Uninsulated;
    })

    this.fields = [
      'insulationType',
      'insulationThickness',
    ]

    this.serFields = this.fields;
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Steel Sectional Door',
    }
  }
}
setupClass(SteelSectionalDoor)

let AircraftHangarDoorInsulationType = makeEnum({
  ExpandedPolystrene: 'Expanded polystrene',
  MineralWoolWithSteelRibs: 'Mineral wool with steel ribs',
  ExtrudedPolystrene: 'Extruded polystrene',
  NoInsulation: 'No insulation',
})

let AircraftHangarDoorInsulationThickness  = makeEnum({
  Four: '4" (102mm)',
  Six: '6" (153mm)',
})

let AircraftHangarDoorApproxSize = makeEnum({
  n72by12: '72ft x 12ft (small private planes)',
  n240by50: '240ft x 50ft (commerical jet)',
})

class AircraftHangarDoor {
  init() {
    let door = this;
    this.insulationType = Field.makeSelect('Insulation type', AircraftHangarDoorInsulationType, {
      note: 'The insulation material used in the aircraft hangar door.',
    })
    this.insulationThickness = Field.makeSelect('Insulation thickness', AircraftHangarDoorInsulationThickness, {
      note: 'The thickness of the insulation. Hidden when the door has no insulation.',
    })
    this.insulationThickness.makeChoicesUpdater(() => {
      if (door.insulationType.value == AircraftHangarDoorInsulationType.NoInsulation) {
        return [];
      } else {
        return makeOptions(AircraftHangarDoorInsulationThickness)
      }
    })
    this.approxSize = Field.makeSelect('Approximate size', AircraftHangarDoorApproxSize, {
      note: 'The approximate size of the hangar door, used to estimate the U-value.',
    })

    this.fields = [
      'insulationType',
      'insulationThickness',
      'approxSize',
    ]

    this.serFields = this.fields;
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Aircraft Hangar Door',
    }
  }
}
setupClass(AircraftHangarDoor)

class DoorBuilder {
  init(doorType) {
    this.doorType = doorType;

    // Note: for the residential app, only swinging door and sliding glass door
    // will be available
    let choices = []
    if (gApp.proj().isResidential()) {
      choices = makeOptions(DoorBuildType, [DoorBuildType.SwingingDoor, DoorBuildType.SlidingGlassDoor])
    } else {
      choices = makeOptions(DoorBuildType)
    }
    this.type = new Field({
      name: 'Door type',
      type: FieldType.Select,
      choices: choices,
      note: 'The construction type of the door. The remaining fields depend on this selection.',
      notesData: {
        'AllOptions': makeOptions(DoorBuildType),
      },
    })
    
    this.swingingDoor = SwingingDoor.create();
    this.slidingGlassDoor = SlidingGlassDoor.create();
    this.stileAndRailDoor = StileAndRailDoor.create();
    this.revolvingDoor = RevolvingDoor.create()
    this.steelEmergencyExitDoor = SteelEmergencyExitDoor.create();
    this.steelSectionalDoor = SteelSectionalDoor.create();
    this.aircraftHangarDoor = AircraftHangarDoor.create();

    let objMap = {
      [DoorBuildType.SwingingDoor]: this.swingingDoor,
      [DoorBuildType.SlidingGlassDoor]: this.slidingGlassDoor,
      [DoorBuildType.StileAndRailDoor]: this.stileAndRailDoor,
      [DoorBuildType.RevolvingDoor]: this.revolvingDoor,
      [DoorBuildType.SteelEmergencyExitDoor]: this.steelEmergencyExitDoor,
      [DoorBuildType.SteelSectional]: this.steelSectionalDoor,
      [DoorBuildType.AircraftHangarDoor]: this.aircraftHangarDoor,
    }
    this.doorType.updater.setMapItemEnabled('door-builder-type', objMap, () => {
      return this.type.value;
    })

    this.serFields = [
      'type',
      'swingingDoor',
      'slidingGlassDoor',
      'stileAndRailDoor',
      'revolvingDoor',
      'steelEmergencyExitDoor',
      'steelSectionalDoor',
      'aircraftHangarDoor',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Door Builder',
    }
  }

  getWindowBuilderIfUsing() {
    if (this.type.value == DoorBuildType.SlidingGlassDoor) {
      return this.slidingGlassDoor.windowBuilder;
    }
    return null;
  }

  getDetailRows() {
    const buildType = this.type.value;
    const rows = [[{ value: 'Door type' }, { value: this.type.getSelectLabelStr() }]];
    if (buildType === DoorBuildType.SwingingDoor) {
      const sd = this.swingingDoor;
      rows.push([{ value: 'Type' }, { value: sd.type.getSelectLabelStr() }]);
      rows.push([{ value: 'Percent glass' }, { value: `${sd.percentGlazing.value}%` }]);
      if (sd.percentGlazing.value > 0) {
        rows.push([{ value: 'Glass type' }, { value: sd.glassType.getSelectLabelStr() }]);
      }
      rows.push([{ value: 'Thermal break' }, { value: sd.thermalBreak.getSelectLabelStr() }]);
    } else if (buildType === DoorBuildType.SlidingGlassDoor) {
      rows.push(...this.slidingGlassDoor.windowBuilder.getDetailRows());
    } else if (buildType === DoorBuildType.StileAndRailDoor) {
      rows.push([{ value: 'Frame type' }, { value: this.stileAndRailDoor.frameType.getSelectLabelStr() }]);
      rows.push([{ value: 'Glass type' }, { value: this.stileAndRailDoor.glassType.getSelectLabelStr() }]);
    } else if (buildType === DoorBuildType.RevolvingDoor) {
      rows.push([{ value: 'Type' }, { value: this.revolvingDoor.type.getSelectLabelStr() }]);
      rows.push([{ value: 'Size' }, { value: this.revolvingDoor.size.getSelectLabelStr() }]);
    } else if (buildType === DoorBuildType.SteelEmergencyExitDoor) {
      const sed = this.steelEmergencyExitDoor;
      rows.push([{ value: 'Insulation' }, { value: sed.insulation.getSelectLabelStr() }]);
      rows.push([{ value: 'Insulation thickness' }, { value: sed.insulationThickness.getSelectLabelStr() }]);
      rows.push([{ value: 'Thermal break' }, { value: sed.thermalBreak.getSelectLabelStr() }]);
      rows.push([{ value: 'Number of doors' }, { value: sed.numDoors.getSelectLabelStr() }]);
    } else if (buildType === DoorBuildType.SteelSectional) {
      const ssd = this.steelSectionalDoor;
      rows.push([{ value: 'Insulation type' }, { value: ssd.insulationType.getSelectLabelStr() }]);
      if (ssd.insulationType.value !== 'Uninsulated') {
        rows.push([{ value: 'Insulation thickness' }, { value: ssd.insulationThickness.getSelectLabelStr() }]);
      }
    } else if (buildType === DoorBuildType.AircraftHangarDoor) {
      const ahd = this.aircraftHangarDoor;
      rows.push([{ value: 'Insulation type' }, { value: ahd.insulationType.getSelectLabelStr() }]);
      if (ahd.insulationType.value !== 'NoInsulation') {
        rows.push([{ value: 'Insulation thickness' }, { value: ahd.insulationThickness.getSelectLabelStr() }]);
      }
      rows.push([{ value: 'Approximate size' }, { value: ahd.approxSize.getSelectLabelStr() }]);
    }
    return rows;
  }
}
setupClass(DoorBuilder)

export class DoorType extends InputComponent {
  init(name, makeId) {
    let doorType = this;

    this.id = makeId ? gApp.proj().makeId('DoorType') : 0;

    this.name = Field.makeName('Name', name)
    this.height = new Field({
      name: 'Height',
      type: FieldType.Length,
      requiresInput: true,
      note: 'The overall height of the door.',
    })
    this.width = new Field({
      name: 'Width',
      type: FieldType.Length,
      requiresInput: true,
      note: 'The overall width of the door.',
    })

    this.addScreenDoor = Field.makeSelect('Screen door', ScreenDoorTypes, {
      note: 'Whether a screen door is installed and, if so, on which side. Affects the SHGC.',
    })
    this.colour = Field.makeSelect('Door Colour', DoorColor, {
      note: 'The exterior colour of the door, used to estimate solar absorptance for the opaque portion.',
    })
    this.inputType = Field.makeSelect('Input mode', DoorInputType, {
      bold: true,
      note: 'The door U-value and SHGC may either be entered manually or calculated by building a door using the door builder.',
    })

    // Manual inputs:
    this.manualUValue = new Field({
      name: 'U-value',
      type: FieldType.UValue,
      min: 0,
      allowMin: false,
      requiresInput: true,
      note: 'The U-value of the opaque portion of the door. Only relevant when input mode is set to Manual.',
    })
    this.percentGlass = new Field({
      name: 'Percent Glass',
      type: FieldType.Percent,
      min: 0,
      value: 0,
      note: 'The percentage of the door area that is glass. Set to 0 if the door is fully opaque. Only relevant when input mode is set to Manual.',
    })
    this.glassDoorUValue = new Field({
      name: 'Glass U-value',
      type: FieldType.UValue,
      min: 0,
      allowMin: false,
      requiresInput: true,
      note: 'The U-value of the glass portion of the door. Only relevant when input mode is Manual and Percent Glass is greater than 0.',
    })
    this.glassDoorShgc = new Field({
      name: 'Glass SHGC',
      type: FieldType.Ratio,
      min: 0,
      allowMin: false,
      requiresInput: false,
      defaultValue: 0.9,
      note: 'The SHGC of the glass portion of the door. Only relevant when input mode is Manual and Percent Glass is greater than 0.',
    })

    // Note: the way we set the field visibility here is a bit gross b/c we have conditionally
    // visible manual fields, but whatever.
    this.manualFields = ['manualUValue', 'percentGlass', 'glassDoorUValue', 'glassDoorShgc']
    this.updater.setFieldsEnabled('manual-fields', this, ['manualUValue', 'percentGlass'], () => {
      return this.inputType.value == DoorInputType.Manual;
    })
    this.updater.setFieldsEnabled('glass-fields', this, ['glassDoorUValue', 'glassDoorShgc'], () => {
      return this.inputType.value == DoorInputType.Manual && this.percentGlass.value > 0;
    })

    this.doorBuilder = DoorBuilder.create(this)
    this.updater.setEnabledWhen('door-builder', this.doorBuilder, () => {
      return this.inputType.value == DoorInputType.BuildDoor;
    })

    this.outputUValue = new Field({
      name: 'Output U-value',
      type: FieldType.UValue,
      isOutput: true,
      allowMin: false,
      note: 'The overall U-value of the door, calculated from your inputs.',
    })
    this.outputUValue.makeUpdater((field) => {
      field.waitingInputs = false;
      if (this.inputType.value == DoorInputType.Manual) {
        let requiredFields = [this.manualUValue];
        if (this.percentGlass.value > 0) {
          requiredFields.push(this.glassDoorUValue);
        }
        field.waitingInputs = Field.checkWaitingInputs(requiredFields);
      } else {
        field.waitingInputs = Field.checkWaitingInputs([
          this.width, this.height,
        ])
      }
      let uValue = doorType.computeUValue();
      field.setValueInUnits(uValue.uValue, Units.UValue);
      field.debugOutput = DebugOn() ? prettyJson(uValue) : null;
    })
    this.outputShgc = new Field({
      name: 'Output SHGC',
      type: FieldType.Ratio,
      isOutput: true,
      note: 'The overall SHGC of the door, calculated from your inputs. N/A if the door has no glass.',
    })
    this.outputShgc.makeUpdater((field) => {
      field.waitingInputs = false;
      if (this.inputType.value == DoorInputType.Manual) {
        if (this.percentGlass.value > 0) {
          field.waitingInputs = Field.checkWaitingInputs([this.glassDoorShgc]);
        } else {
          field.waitingInputs = false;
        }
      } else {
        field.waitingInputs = Field.checkWaitingInputs([
          this.width, this.height,
        ]);
      }
      let shgcs = doorType.computeShgc()
      field.value = shgcs.shgc;
      field.debugOutput = DebugOn() ? prettyJson(shgcs) : null;
    })

    this.serFields = [
      'id',
      'name',
      'height',
      'width',
      'addScreenDoor',
      'colour',
      'inputType',
      'manualUValue',
      'percentGlass',
      'glassDoorUValue',
      'glassDoorShgc',
      'doorBuilder',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Door Type',
      uniquePath: true,
      path: () => {
        return `doors/${this.id}`;
      },
    }
  }

  static getTypeMetadata() {
    return {
      name: 'Door',
      createType: DoorType.createType,
      openEditor: DoorType.openEditor,
      customEditor: true,
    }
  }

  static createType() {
    let type = DoorType.create(generateItemName('Door', gApp.proj().doorTypes), true)
    gApp.proj().doorTypes.push(type);
    sortItemsByName(gApp.proj().doorTypes);
    return type
  }

  static openEditor(doorType) {
    let pathName = gApp.proj().isCommercial() ? 'building-doors' : 'doors';
    gApp.proj().editorState.selectedDoor = doorType;
    gApp.router.push({name: pathName});
  }

  static getTableInfo() {
    return {
      typeName: 'Door',
      pagePath: 'doors',
      allowDuplicate: true,
      columns: {
        name: {label: 'Name'},
        style: {label: 'Style'},
        height: {label: 'Height'},
        width: {label: 'Width'},
        uValue: {label: 'U-Value'},
        shgc0: {label: 'SHGC (0 deg)'},
        shgcDiff: {label: 'SHGC (Diff.)'},
      }
    }
  }

  getTableData() {
    let shgc0 = "--";
    let shgcDiff = "--";
    if (!this.outputShgc.isWaitingInputs()) {
      shgc0 = this.getShgc0Deg();
      shgcDiff = this.getShgcDiffuse();
    }
    return {
      name: this.name.value,
      style: this.getDoorTypeStr(),
      height: this.height.getDisplayValueStr(),
      width: this.width.getDisplayValueStr(),
      uValue: this.outputUValue.getDisplayValueStr(),
      shgc0: shgc0,
      shgcDiff: shgcDiff,
    }
  }

  getShgc0Deg() {
    return resOr(() => {
      let shgc = this.computeShgc();
      return !shgc.isNA ? formatNum(shgc.Deg0) : "N/A"
    }, 'Error');
  }

  getShgcDiffuse() {
    return resOr(() => {
      let shgc = this.computeShgc();
      return !shgc.isNA ? formatNum(shgc.Diffuse) : "N/A"
    }, 'Error');
  }

  getComponentTableData() {
    return {
      columns: [
        { name: 'Name' },
        { name: 'U-value', units: this.outputUValue.getDisplayUnitsLabel() },
        { name: 'SHGC (0°)' },
        { name: 'SHGC (Diffuse)' },
        { name: 'Height', units: this.height.getDisplayUnitsLabel() },
        { name: 'Width', units: this.width.getDisplayUnitsLabel() },
        { name: 'Glass %' },
      ],
      rows: [[
        { value: this.name.value },
        { value: this.outputUValue.getDisplayValueStr() },
        { value: this.getShgc0Deg() },
        { value: this.getShgcDiffuse() },
        { value: this.height.getDisplayValueStr() },
        { value: this.width.getDisplayValueStr() },
        { value: `${formatNum(this.getPercentGlass())}%` },
      ]],
    };
  }

  getConstructionDetailsData() {
    const uValueUnits = this.outputUValue.getDisplayUnitsLabel();
    if (this.isManualEntry()) {
      return {
        columns: [{ name: 'Property' }, { name: 'Value' }],
        rows: [
          [{ value: 'Entry method' }, { value: 'Manual entry' }],
          [{ value: 'U-value', isBold: true }, { value: this.outputUValue.getDisplayValueStr(), units: uValueUnits, isBold: true }],
        ],
        showHeader: false,
      };
    }
    const rows = this.doorBuilder.getDetailRows();
    rows.push([
      { value: 'U-value', isBold: true },
      { value: this.outputUValue.getDisplayValueStr(), units: uValueUnits, isBold: true },
    ]);
    return {
      columns: [{ name: 'Property' }, { name: 'Value' }],
      rows,
      showHeader: false,
    };
  }

  setFromExcelData(data, importer) {
    importer.applyData(data, {
      'Name': (value) => {
        this.name.setValueAsUser(value);
      },
      'Height [ft]': (value) => {
        console.log(`Setting door height from "${value}"`);
        this.height.setValueAsUser(importer.parseFeetValue(value));
      },
      'Width [ft]': (value) => {
        console.log(`Setting door width from "${value}"`);
        this.width.setValueAsUser(importer.parseFeetValue(value));
      },
      'U-value': (value) => {
        this.inputType.value = DoorInputType.Manual;
        console.log(`Setting door U-value from "${value}"`);
        this.manualUValue.setValueAsUser(parseFloat(value));
      },
      'Colour': (value) => {
        let enumValue = importer.parseEnumValue(DoorColor, value);
        if (enumValue) {
          this.colour.setValueAsUser(enumValue);
        }
      },
      'Glass %': (value) => {
        this.inputType.value = DoorInputType.Manual;
        this.percentGlass.setValueAsUser(parseFloat(value));
      },
      'Glass U-value': (value) => {
        this.inputType.value = DoorInputType.Manual;
        this.glassDoorUValue.setValueAsUser(parseFloat(value));
      },
      'Glass SHGC [0 deg]': (value) => {
        this.inputType.value = DoorInputType.Manual;
        this.glassDoorShgc.setValueAsUser(parseFloat(value));
      }
    });
  }

  getInputPage() {
    return {
      label: `${this.name.value}`,
      // TODO
      path: `doors/${this.id}`,
      typeName: 'Door',
      typeObject: this,
    };
  }

  getArea() {
    return this.height.getValueInUnits(Units.ft) * this.width.getValueInUnits(Units.ft);
  }

  getPerimeter() {
    return 2 * (this.height.getValueInUnits(Units.ft) + this.width.getValueInUnits(Units.ft));
  }

  getOpaqueArea() {
    return this.getArea() * (1.0 - this.getPercentGlass() / 100.0);
  }

  getGlassArea() {
    return this.getArea() * (this.getPercentGlass() / 100.0);
  }

  hasScreenDoor() {
    return this.addScreenDoor.value != ScreenDoorTypes.None;
  }

  getWindowStyle() {
    // We assume that for doors with glass, the window-style is operable
    return WindowStyle.Operable;
  }

  getWindowBuilderIfUsing() {
    if (this.inputType.value == DoorInputType.BuildDoor) {
      return this.doorBuilder.getWindowBuilderIfUsing();
    }
    return null;
  }

  isManualEntry() {
    return this.inputType.value == DoorInputType.Manual;
  }

  getDoorTypeStr() {
    return this.isManualEntry() ? "N/A" : DoorBuildType._labels[this.doorBuilder.type.value];
  }

  // Percent by area, [0-100]
  getPercentGlass() {
    if (this.inputType.value == DoorInputType.Manual) {
      return this.percentGlass.value;
    } else {
      let type = this.doorBuilder.type.value;
      if (type == DoorBuildType.SwingingDoor) {
        return this.doorBuilder.swingingDoor.percentGlazing.value;
      } else if (type == DoorBuildType.SlidingGlassDoor) {
        return 100.0;
      } else if (type == DoorBuildType.StileAndRailDoor) {
        return 100.0;
      } else {
        // Assume other types have no glass
        console.log(`Other door type: ${type}`);
        return 0;
      }
    }
  }

  computeUValue() {
    if (this.inputType.value == DoorInputType.Manual) {
      let percentGlass = this.percentGlass.value / 100.0;
      let uValueDoor = this.manualUValue.getValueInUnits(Units.UValue);
      let uValueGlass = this.glassDoorUValue.getValueInUnits(Units.UValue);
      let uValue = uValueDoor * (1 - percentGlass) + uValueGlass * percentGlass;
      return {uValue, uValueDoor, uValueGlass};
    } else {
      let builder = this.doorBuilder;
      let doorType = builder.type.value;
      if (doorType == DoorBuildType.SwingingDoor) {
        let door = builder.swingingDoor;
        let uValueDoor = lookupData(DoorsData, ['SlabDoor', door.type.value])
        let percentGlass = door.percentGlazing.value / 100.0;
        if (percentGlass == 0) {
          return {uValue: uValueDoor, uValueDoor, uValueGlass: 0};
        } else {
          // Note: these are row nums, not row ids
          let glassRowMap = {
            SinglePane: 2,
            DoublePane: 7,
            DoublePaneLowE: 24,
          };
          let glassRow = lookupData(glassRowMap, [door.glassType.value])

          let glassCol = null;
          let detailedDoorType = door.type.value;
          let thermalBreak = door.thermalBreak.value == YesNo.Yes;
          if (detailedDoorType == 'WoodSlabInWoodFrame') {
            glassCol = 'I';
          } else if (detailedDoorType == 'InsulatedSteelSlabWithWoodEdgeInWoodFrame') {
            glassCol = 'I';
          } else if (detailedDoorType == 'InsulatedSteelSlabWithMetalEdgeInMetalFrame' ||
            detailedDoorType == 'CardboardHoneycombSlab' || detailedDoorType == 'StileAndRailDoor') {
            if (thermalBreak) {
              glassCol = 'G';
            } else  {
              glassCol = 'F';
            }
          }
          if (!glassCol) {
            throw new Error("Unexpected: glassCol not found for door type: " + detailedDoorType);
          }

          let uValueGlass = gApp.proj().windowsData.uValues.lookupValue(glassRow, glassCol);
          let uValue = uValueDoor * (1 - percentGlass) + uValueGlass * percentGlass;
          return {uValue, uValueDoor, uValueGlass, debug: {uValueDoor, uValueGlass, percentGlass, glassRow, glassCol}}
        }
      } else if (doorType == DoorBuildType.SlidingGlassDoor) {
        // Treat like a window type. WindowStyle is assumed to be Operable.
        let door = builder.slidingGlassDoor;
        let winBuilder = door.windowBuilder;

        // console.log("WinBuilder: ", winBuilder);
        let inputData = {
          width: this.width.getValueInUnits(Units.ft),
          height: this.height.getValueInUnits(Units.ft),
          frameWidth: winBuilder.frameWidth.getValueInUnits(Units.ft),
          numPanes: winBuilder.numPanes.value,
          glazing: winBuilder.glazing.value,
          panesAppliedTo: winBuilder.panesAppliedTo.value,
          gapType: winBuilder.gapType.value,
          windowStyle: WindowStyle.Operable,
          frameStyle: winBuilder.frameStyle.value,
          windowsData: gApp.proj().windowsData,
        };
        let res = WindowType.computeUValue(inputData);
        return {...res, uValueGlass: res.uValue, uValueDoor: 0};
      } else if (doorType == DoorBuildType.StileAndRailDoor) {
        let door = builder.stileAndRailDoor;
        // Note: these are row-nums, not ids
        let glassRowMap = {
          SinglePane: 2,
          DoublePane: 12,
          DoublePaneLowE: 29,
        };
        let glassRow = lookupData(glassRowMap, [door.glassType.value])

        let glassCol = null;
        let frameType = door.frameType.value;
        if (frameType == AluminumDoorFrameType.AluminumWithoutThermalBreak) {
          glassCol = 'F';
        } else if (frameType == AluminumDoorFrameType.AluminumWithThermalBreak) {
          glassCol = 'G';
        }
        if (!glassCol) {
          throw new Error("Unexpected: glassCol not found for frame type: " + frameType);
        }

        let uValueGlass = gApp.proj().windowsData.uValues.lookupValue(glassRow, glassCol);
        //console.log(`U-Value Glass: ${uValueGlass}`);
        return {uValue: uValueGlass, uValueDoor: 0, uValueGlass, debug: {uValueGlass, glassRow, glassCol}}
      } else if (doorType == DoorBuildType.RevolvingDoor) {
        let door = builder.revolvingDoor;
        let uValueDoor = lookupData(DoorsData,
          ['RevolvingDoor', door.type.value, door.size.value])
        return {uValue: uValueDoor, uValueDoor, uValueGlass: 0};
      } else if (doorType == DoorBuildType.SteelEmergencyExitDoor) {
        let door = builder.steelEmergencyExitDoor;
        let thermalBreakField = door.thermalBreak.value == YesNo.Yes ? 'WithThermalBreak' : 'WithoutThermalBreak';
        let uValueDoor = lookupData(DoorsData,
          ['SteelEmergencyExitDoor', thermalBreakField, door.insulationThickness.value,
          door.insulation.value, door.numDoors.value]);
        return {uValue: uValueDoor, uValueDoor, uValueGlass: 0};
      } else if (doorType == DoorBuildType.SteelSectional) {
        let door = builder.steelSectionalDoor;
        let uValueDoor = lookupData(DoorsData, [
          'SteelSectional',
          door.insulationThickness.value,
          door.insulationType.value,
        ]);
        return {uValue: uValueDoor, uValueDoor, uValueGlass: 0};
      } else if (doorType == DoorBuildType.AircraftHangarDoor) {
        let door = builder.aircraftHangarDoor;
        let uValueDoor = lookupData(DoorsData, [
          'AircraftHangarDoor',
          door.insulationThickness.value,
          door.insulationType.value,
          door.approxSize.value,
        ]);
        return {uValue: uValueDoor, uValueDoor, uValueGlass: 0};
      } else {
        throw new Error("Unknown door type: " + doorType);
      }
    }
  }

  computeShgc() {
    if (this.inputType.value == DoorInputType.Manual) {
      if (this.percentGlass.value > 0) {
        let uValue = this.computeUValue().uValue;
        let shgcs = WindowType._estimateShgcs(this.glassDoorShgc.value, uValue);
        return {...shgcs, isNA: false};
      } else {
        // NA
        return {shgc: 0, isNA: true};
      }
    } else {
      let rowsMap = {
        SinglePane: '1b',
        DoublePane: '5b',
        DoublePaneLowE: '17d',
      };
      let builder = this.doorBuilder;
      let doorType = builder.type.value;
      if (doorType == DoorBuildType.SwingingDoor) {
        let door = builder.swingingDoor;
        if (door.percentGlazing.value == 0) {
          // NA
          console.log("Percent glazing: 0");
          return {shgc: 0, isNA: true};
        }
        let colChar = null;
        let detailedDoorType = door.type.value;
        if (detailedDoorType == SwingingDoorType.WoodSlabInWoodFrame ||
            detailedDoorType == SwingingDoorType.InsulatedSteelSlabWithWoodEdgeInWoodFrame) {
          colChar = 'R';
        } else {
          colChar = 'P';
        }
        let rowId = lookupData(rowsMap, [door.glassType.value])
        let shgcs = WindowType._lookupShgcs(gApp.proj().windowsData, rowId, colChar);
        WindowType.normalizeShgcValues(shgcs);
        return {
          ...shgcs,
          isNA: false,
          debug: {
            rowId: rowId,
            colChar: colChar
          },
        };
      } else if (doorType == DoorBuildType.StileAndRailDoor) {
        let door = builder.stileAndRailDoor;
        if (door.getPercentGlazing() == 0) {
          // NA
          return {shgc: 0, isNA: true};
        }
        let colChar = 'P';
        let rowId = lookupData(rowsMap, [door.glassType.value]);
        let shgcs = WindowType._lookupShgcs(gApp.proj().windowsData, rowId, colChar);
        WindowType.normalizeShgcValues(shgcs);
        return {
          ...shgcs,
          isNA: false,
          debug: {
            rowId: rowId,
            colChar: colChar
          },
        }
      } else if (doorType == DoorBuildType.SlidingGlassDoor) {
        // Do the same way as for windows. WindowStyle is assumed to be Operable.
        let door = builder.slidingGlassDoor;
        let winBuilder = door.windowBuilder;

        let inputData = {
          width: this.width.getValueInUnits(Units.ft),
          height: this.height.getValueInUnits(Units.ft),
          frameWidth: winBuilder.frameWidth.getValueInUnits(Units.ft),
          numPanes: winBuilder.numPanes.value,
          glazing: winBuilder.glazing.value,
          panesAppliedTo: winBuilder.panesAppliedTo.value,
          paneThickness: winBuilder.paneThickness.value,
          tint: winBuilder.tint.value,
          gapType: winBuilder.gapType.value,
          windowStyle: WindowStyle.Operable,
          frameStyle: winBuilder.frameStyle.value,
          windowsData: gApp.proj().windowsData,
        };
        return {...WindowType.computeShgc(inputData), isNA: false};
      } else {
        // NA
        return {shgc: 0, isNA: true};
      }
    }
  }
};
setupClass(DoorType);
